import type {
	CellClassParams,
	ColDef,
	ValueGetterParams,
} from "ag-grid-community";
import { MILL_2 } from "@/setup/mill-config/mill-config";
import {
	EDGER_ORIENTATION_OPTIONS,
	STAND_TYPE_OPTIONS,
} from "../../../../models/Section";
import type {
	SectionExportStand,
	SectionExportStandSetup,
	SectionsExportData,
} from "../../models/SectionExport";
import { EXCEL_STYLE, isBanded, NOT_APPLICABLE } from "./sectionExportStyles";

interface StandSetupField {
	label: string;
	cellClass: string[];
	getValue: (setup: SectionExportStandSetup) => string | number | null;
	isNotApplicable?: (setup: SectionExportStandSetup) => boolean;
}

const STAND_SETUP_FIELDS: StandSetupField[] = [
	{
		label: "Setpoint Gap",
		cellClass: [EXCEL_STYLE.PRECISE],
		getValue: (setup) => setup.SetpointGap,
	},
	{
		label: "Type",
		cellClass: [EXCEL_STYLE.TEXT, EXCEL_STYLE.CENTER],
		getValue: (setup) =>
			STAND_TYPE_OPTIONS.find((option) => option.code === setup.StandTypeCode)
				?.name ?? null,
	},
	{
		label: "Width",
		cellClass: [EXCEL_STYLE.PRECISE],
		getValue: (setup) => setup.WidthMeasurement,
		isNotApplicable: (setup) => setup.StandTypeCode !== "U",
	},
	{
		label: "Orientation",
		cellClass: [EXCEL_STYLE.TEXT, EXCEL_STYLE.CENTER],
		getValue: (setup) =>
			EDGER_ORIENTATION_OPTIONS.find(
				(option) => option.code === setup.EdgerOrientationCode,
			)?.name ?? null,
		isNotApplicable: (setup) => setup.StandTypeCode !== "E",
	},
	{
		label: "Area",
		cellClass: [EXCEL_STYLE.PRECISE],
		getValue: (setup) => setup.Area,
	},
];

const SECTION_NAME_FIELD_INDEX = Math.floor(
	(STAND_SETUP_FIELDS.length - 1) / 2,
);

export interface StandSetupsSheetRow {
	SectionName: string;
	Field: StandSetupField;
	FieldIndex: number;
	SetupsByStandId: Map<number, SectionExportStandSetup>;
	GroupIndex: number;
}

export const buildStandSetupsSheetRows = (
	data: SectionsExportData,
	sectionIds: number[],
): StandSetupsSheetRow[] => {
	const m2SectionsById = new Map(
		data.sections
			.filter((section) => section.AssetId === MILL_2.id)
			.map((section) => [section.SectionId, section]),
	);
	const setupsBySectionId = new Map<
		number,
		Map<number, SectionExportStandSetup>
	>();
	for (const setup of data.standSetups) {
		const setups = setupsBySectionId.get(setup.SectionId) ?? new Map();
		setups.set(setup.StandId, setup);
		setupsBySectionId.set(setup.SectionId, setups);
	}

	return sectionIds
		.flatMap((sectionId) => {
			const section = m2SectionsById.get(sectionId);
			return section ? [section] : [];
		})
		.flatMap((section, groupIndex) =>
			STAND_SETUP_FIELDS.map((field, fieldIndex) => ({
				SectionName: section.SectionName,
				Field: field,
				FieldIndex: fieldIndex,
				SetupsByStandId: setupsBySectionId.get(section.SectionId) ?? new Map(),
				GroupIndex: groupIndex,
			})),
		);
};

const sectionBlockStyle = (fieldIndex: number) =>
	fieldIndex === 0
		? EXCEL_STYLE.SECTION_BLOCK_TOP
		: fieldIndex === STAND_SETUP_FIELDS.length - 1
			? EXCEL_STYLE.SECTION_BLOCK_BOTTOM
			: EXCEL_STYLE.SECTION_BLOCK_MIDDLE;

const isStandNotApplicable =
	(standId: number) => (params: CellClassParams<StandSetupsSheetRow>) => {
		const setup = params.data?.SetupsByStandId.get(standId);
		return (
			setup !== undefined &&
			(params.data?.Field.isNotApplicable?.(setup) ?? false)
		);
	};

const standColumn = (
	stand: SectionExportStand,
): ColDef<StandSetupsSheetRow> => ({
	headerName: stand.StandName,
	width: 90,
	valueGetter: (params: ValueGetterParams<StandSetupsSheetRow>) => {
		const setup = params.data?.SetupsByStandId.get(stand.StandId);
		if (!params.data || !setup) return null;
		if (params.data.Field.isNotApplicable?.(setup)) return NOT_APPLICABLE;
		return params.data.Field.getValue(setup);
	},
	cellClass: (params) => params.data?.Field.cellClass ?? [],
	cellClassRules: {
		[EXCEL_STYLE.BAND]: isBanded<StandSetupsSheetRow>,
		[EXCEL_STYLE.NOT_APPLICABLE]: isStandNotApplicable(stand.StandId),
	},
});

export const buildStandSetupsSheetColumns = (
	stands: SectionExportStand[],
): ColDef<StandSetupsSheetRow>[] => [
	{
		headerName: "Section",
		width: 160,
		pinned: "left",
		valueGetter: (params) =>
			params.data?.FieldIndex === SECTION_NAME_FIELD_INDEX
				? params.data.SectionName
				: null,
		cellClass: (params) => [
			sectionBlockStyle(params.data?.FieldIndex ?? 0),
			EXCEL_STYLE.SECTION,
		],
		cellClassRules: { [EXCEL_STYLE.BAND]: isBanded<StandSetupsSheetRow> },
	},
	{
		headerName: "Field",
		width: 110,
		pinned: "left",
		valueGetter: (params) => params.data?.Field.label ?? null,
		cellClass: [EXCEL_STYLE.TEXT, EXCEL_STYLE.FIELD],
		cellClassRules: { [EXCEL_STYLE.BAND]: isBanded<StandSetupsSheetRow> },
	},
	...stands.map(standColumn),
];
